export interface CartItem {
  variantId: string;
  productTitle: string;
  variantTitle: string;
  price: number;
  quantity: number;
  colorCode: string;
  handle: string;
  imageUrl?: string;
}

export const MAX_CART_QUANTITY = 10;

const VARIANT_GID = /^gid:\/\/shopify\/ProductVariant\/\d+$/;
const HANDLE = /^[\p{L}\p{N}_-]+$/u;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toText = (value: unknown): string => (value == null ? '' : String(value));

const toQuantity = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 1;
  return Math.min(MAX_CART_QUANTITY, Math.max(1, Math.floor(value)));
};

const toHttpsUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  try {
    return new URL(value).protocol === 'https:' ? value : undefined;
  } catch {
    return undefined;
  }
};

const toCartItem = (value: unknown): CartItem | null => {
  if (!isRecord(value)) return null;
  const { variantId, handle, price } = value;
  if (typeof variantId !== 'string' || !VARIANT_GID.test(variantId)) return null;
  if (typeof handle !== 'string' || !HANDLE.test(handle)) return null;
  if (typeof price !== 'number' || !Number.isFinite(price) || price < 0) return null;

  const item: CartItem = {
    variantId,
    productTitle: toText(value.productTitle),
    variantTitle: toText(value.variantTitle),
    price,
    quantity: toQuantity(value.quantity),
    colorCode: toText(value.colorCode),
    handle,
  };
  const imageUrl = toHttpsUrl(value.imageUrl);
  if (imageUrl) item.imageUrl = imageUrl;
  return item;
};

export const parseStoredCartItems = (raw: string | null | undefined): CartItem[] => {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const byVariant = new Map<string, CartItem>();
  for (const entry of parsed) {
    const item = toCartItem(entry);
    if (!item) continue;
    const existing = byVariant.get(item.variantId);
    if (existing) {
      existing.quantity = Math.min(MAX_CART_QUANTITY, existing.quantity + item.quantity);
    } else {
      byVariant.set(item.variantId, item);
    }
  }
  return [...byVariant.values()];
};
